/**
 * Expectations Bar — how much good news is already priced into a stock going
 * into its earnings print. 0 = washed out, 100 = crowded.
 *
 * DESCRIPTIVE, NOT PREDICTIVE. Every input here is a *positioning* measure:
 * where the stock sits in its range, how the tape feels, how options are
 * leaning. None of them carry information about whether the company will beat
 * estimates, so this score deliberately makes no claim about the direction of
 * the post-earnings move. It answers only "how high is the bar this stock has
 * to clear?" — the user supplies the directional read.
 *
 * CALIBRATED ONCE, ONE REGIME (issue #107). Weights and the implied-move
 * shrink factor were fitted against 3,369 labeled reactions Apr–Aug 2026
 * (the window recoverable after issue #97 unfroze `earnings_history`). The
 * study's validated meaning is worth internalising: a HIGH bar predicts a
 * SMALLER, slightly weaker reaction — priced-in, literally — and a LOW bar a
 * larger, upward-tilted one. That is one stretch of tape with no bear regime
 * in it; do not re-tune any constant here without a fresh study, and re-run
 * the study after the Nov–Dec 2026 cycle (sector sentiment will then have
 * history at event dates for the first time).
 *
 * Lives in `src/shared` (rather than `workers/shared`) because BOTH trees need
 * it and only this direction works: CRA's module scope forbids the frontend
 * importing from outside `src/`, while wrangler happily bundles frontend-tree
 * files into a worker. Same arrangement as `src/shared/bandSignals.js` and
 * `src/shared/sentimentBands.js`.
 *
 * Consumers:
 *  - `workers/screen_query/src/index.js` — computes the score per screened row
 *  - `src/components/CompanyRow.js` — renders the cell, colouring it from
 *    `BAR_BANDS` so the colour ramp can never disagree with the rating label
 *
 * @see issue #98 (original design), issue #107 (calibration)
 */

import { ratingFor } from './sentimentBands.js';

/**
 * Where a 0-100 bar score stops being washed out and starts being crowded.
 * Deliberately symmetric around 50 so "neutral" is a genuine middle rather
 * than a bucket that swallows most of the universe.
 *
 * Same shape as `RATING_BANDS` in `./sentimentBands.js`, and read by the same
 * `ratingFor` lookup — only the thresholds and labels differ, because a
 * positioning score is not a sentiment score.
 */
export const BAR_BANDS = [
	{ maxExclusive: 20, rating: 'washed out' },
	{ maxExclusive: 40, rating: 'low bar' },
	{ maxExclusive: 60, rating: 'neutral' },
	{ maxExclusive: 80, rating: 'high bar' },
	{ maxExclusive: Infinity, rating: 'crowded' }
];

/** @param {number} score 0-100 @returns {string} band label */
export function barRatingFor(score) {
	return ratingFor(score, BAR_BANDS);
}

/**
 * Component weights — CALIBRATED against the 2026-08-12 study (issue #107):
 * 3,369 labeled reactions Apr–Aug 2026 from the revived `earnings_history`.
 * One regime of tape; do not re-tune without a fresh study (next natural
 * checkpoint: after the Nov–Dec 2026 earnings cycle, when sector sentiment
 * will finally have history at event dates).
 *
 * What the study measured (rank correlation vs signed / absolute reaction):
 *  - rangePosition  −0.039 / −0.195  — the real signal. Monotone in
 *    magnitude (bottom-quintile stocks react ~9.3%, top-quintile ~6.0%),
 *    mild contrarian tilt at the extremes; robust across price bands and
 *    months. Carries the dominant weight.
 *  - runUp (new)    −0.037 / −0.045  — the linear correlation understates it:
 *    magnitude is U-shaped (both big rallies and big sell-offs precede large
 *    reactions), and the fade tilt (top quintile 47.5% up, median −0.4%) is
 *    exactly "already priced in" evidence. Modest weight, because a linear
 *    0-100 map captures only the fade dimension of a U-shaped effect.
 *  - optionsPositioning +0.003 / −0.003 — DEAD in the data. Kept at token
 *    weight for display continuity, not because it earned it.
 *  - marketSentiment +0.024 / −0.006 — also dead in-window; it's one global
 *    scalar so it never discriminated cross-sectionally anyway. Token weight;
 *    its value is making the absolute level readable (70 in a greedy tape is
 *    not 70 in a fearful one).
 *  - sectorSentiment — UNTESTABLE (no history existed at any event date).
 *    Weight unchanged pending the next study, on the same reasoning it was
 *    assigned originally.
 *
 * These are renormalised over whatever components are actually present, so
 * they express *relative* importance rather than absolute contribution.
 */
export const COMPONENT_WEIGHTS = {
	rangePosition: 0.50,
	runUp: 0.15,
	sectorSentiment: 0.15,
	optionsPositioning: 0.10,
	marketSentiment: 0.10
};

/** How many components a fully-covered row can have. @see coverage */
export const COMPONENT_COUNT = Object.keys(COMPONENT_WEIGHTS).length;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

const isPositiveNumber = value => isFiniteNumber(value) && value > 0;

/**
 * The scale every component mapper shares: 0-100 centred on 50, saturating
 * once the deviation reaches ±`span`. Centred rather than min/max-normalised
 * so that "no deviation" lands on a genuine neutral instead of drifting with
 * whatever the extremes of the day happen to be.
 *
 * Each caller supplies its own `span` (and its derivation) — this only owns
 * the shape, so the two mappers can never drift apart on it.
 */
const centredScore = (deviation, span) => clamp(50 + 50 * (deviation / span), 0, 100);

/**
 * Pass-through for inputs that are already 0-100 (the sentiment scores).
 * Clamps defensively and preserves absence as null — a 0 is a real reading
 * ("maximum fear"), so `??`-style handling everywhere, never `||`.
 */
const sentimentScore = value => (isFiniteNumber(value) ? clamp(value, 0, 100) : null);

/**
 * Position within the 52-week range, as 0-100. A stock at its high has the
 * most priced in; one at its low has the least.
 *
 * Returns null rather than a midpoint when the range is degenerate (high <=
 * low happens on freshly-listed tickers and on rows whose 52-week fields were
 * never written) — a fabricated 50 would read as a real "neutral" claim.
 */
export function rangePositionScore(price, week52Low, week52High) {
	if (!isPositiveNumber(price) || !isPositiveNumber(week52Low) || !isPositiveNumber(week52High)) return null;
	if (week52High <= week52Low) return null;
	return clamp(((price - week52Low) / (week52High - week52Low)) * 100, 0, 100);
}

/**
 * Call/put open-interest skew as 0-100. Heavy call positioning means the
 * bullish case is already crowded; heavy puts mean it is not.
 *
 * Open interest rather than volume: volume is dominated by a handful of
 * strikes on any given day and is far noisier at this aggregation level.
 *
 * Log-scaled because the ratio is multiplicative — 4:1 calls and 1:4 puts are
 * equal-and-opposite, which a linear map would badly distort. A 4x skew in
 * either direction saturates the scale (log2(4) = 2, hence the span).
 */
const OI_SKEW_SPAN_LOG2 = 2;

export function optionsPositioningScore(callOpenInterest, putOpenInterest) {
	if (!isPositiveNumber(callOpenInterest) || !isPositiveNumber(putOpenInterest)) return null;
	return centredScore(Math.log2(callOpenInterest / putOpenInterest), OI_SKEW_SPAN_LOG2);
}

/**
 * A ±15% four-week move saturates the run-up scale. The study's quintile cuts
 * were −7.0% / −0.8% / +3.4% / +9.7%, so ±15% puts the observed extremes near
 * the ends of the scale without letting a single meme-stock double pin it.
 */
const RUN_UP_SPAN = 0.15;

/**
 * Price change over roughly the last four weeks, as 0-100. A stock that has
 * already rallied hard into its print arrives with that rally priced in; one
 * that sold off arrives with expectations reset.
 *
 * The fractional change is passed in pre-computed: screen_query derives it
 * from the same value it serves as `price_change_1m`, so the column a user
 * sorts on and this component always agree.
 *
 * Note the deliberate mismatch with the study: run-up's magnitude effect is
 * U-shaped (both extremes precede big moves), which a linear map cannot
 * express. This component encodes only the "priced in" / fade dimension,
 * which IS monotone — hence its modest weight.
 */
export function runUpScore(runUp) {
	if (!isFiniteNumber(runUp)) return null;
	return centredScore(runUp, RUN_UP_SPAN);
}

/**
 * Beyond this horizon the figure stops being an *earnings* implied move.
 *
 * `options-snapshot-cron` targets the first expiry after the print, which is
 * almost always near-dated: 93% of current snapshots expire within 45 days.
 * The residual tail is not an earnings instrument at all, and because the move
 * scales with sqrt(T) a long expiry produces an absurd headline number — a
 * ~7-month expiry on a high-IV biotech rendered as "±74%", which reads as
 * broken rather than as the different quantity it actually is. Better to show
 * nothing than a number the user would have to know to discount.
 */
const MAX_IMPLIED_MOVE_HORIZON_DAYS = 60;

/**
 * Data-quality gate on the stored IV averages.
 *
 * `options_snapshots` holds a volume-weighted IV across *all* strikes, not the
 * ATM straddle. On liquid names volume concentrates near the money and the
 * aggregate lands close to ATM — F prices out at a believable ±4.4%. On
 * illiquid names volume is thin and scattered, so deep-OTM strikes (where a
 * one-cent bid implies enormous vol) dominate the average and the result is
 * meaningless: ABEO stored a 713% put IV against a 186% call IV, and its call
 * IV swung 616% -> 186% overnight.
 *
 * Two gates, both measured on live data 2026-08-11:
 *  - MAX: 18% of fresh snapshots carry a call IV above 150%, which is not a
 *    credible ATM vol even for a biotech into a binary event.
 *  - DIVERGENCE: calls and puts on the same underlying and expiry should imply
 *    similar vol. When they disagree by more than 2x, the average is being
 *    driven by wing noise rather than by a real market. This is the stronger
 *    signal because it is self-validating — it needs no view on what IV level
 *    is "reasonable" for a given name.
 *
 * Both IVs are required: a single-sided reading has nothing to cross-check.
 */
const MAX_CREDIBLE_IV = 1.5;
const MAX_IV_DIVERGENCE = 2.0;

/**
 * Low-side gate: the stored IV must not sit implausibly far below the stock's
 * OWN recent realized volatility. Issue #103.
 *
 * The same all-strike averaging that inflates IV on some names deflates it on
 * others, and the high-side gates above are blind to that direction. Measured
 * 2026-08-12: AMCR stored ~7.1% IV against 35.4% realized (0.20x) and BETA
 * ~11.5% against 82.1% (0.14x) — both sailed through, because their call/put
 * legs agreed with each other. Two wrong numbers that agree still pass a
 * divergence check; agreement is not accuracy.
 *
 * This is deliberately RELATIVE, not an absolute floor. A flat minimum would
 * be wrong in both directions: it would delete BOND (7.6% IV against 4.8%
 * realized — a bond ETF that really is that calm, ratio 1.58) while still
 * admitting a sleepy-looking reading on a genuinely volatile name.
 *
 * 0.5 is set well below parity because IV legitimately sits under trailing
 * realized after a shock has rolled out of the window; it is meant to catch
 * the 5-7x discrepancies above, not to police the variance risk premium. No
 * high-side realized check: IV running 2-4x realized into a print is normal,
 * and MAX_CREDIBLE_IV already backstops the extreme.
 *
 * A falsely calm number is the more dangerous failure. An absurd ±74% gets
 * dismissed on sight; a plausible-looking ±1.4% on a stock that moves 35%
 * annualized invites trust it hasn't earned.
 */
const MIN_IV_TO_REALIZED = 0.5;

/**
 * Empirical shrink applied to the raw straddle arithmetic so the displayed
 * band is a TRUE 1σ against measured reactions. Issue #107.
 *
 * The raw all-strike figure is grossly overstated: on the study's gated set
 * (1,717 events, Apr–Aug 2026) the median realized/implied ratio was 0.34 and
 * 90.7% of reactions landed inside the "1σ" band — it was closer to a 2σ
 * claim wearing a 1σ label. Fitting the factor so the band contains 68% of
 * realized reactions gives 0.504; at 0.50 measured coverage is 67.6%, median
 * displayed value ±7.9%, p90 ±13.3%.
 *
 * Two sources of the overstatement, in roughly equal parts: the volume-
 * weighted all-strike IV sits above ATM (OTM wings), and the expiry window is
 * longer than the single-session reaction the label measures. The ATM-straddle
 * successor design eliminates the first at the source; until then this
 * constant is the honest correction. Single-regime fit — re-derive alongside
 * any weight re-tune, and DELETE when ATM data replaces the all-strike feed.
 */
const IMPLIED_MOVE_CALIBRATION = 0.5;

/**
 * Expected move implied by the options market, as a percentage of spot,
 * empirically calibrated (see IMPLIED_MOVE_CALIBRATION) so that roughly 68%
 * of measured post-earnings reactions land inside the ±band — a true 1σ.
 *
 * The result is not annualised, and it is not a direction.
 *
 * @param {number} [inputs.realizedVol] annualised realized vol as a fraction
 *   (0.35 = 35%). Optional: when absent the low-side gate cannot run and is
 *   skipped, leaving the absolute and divergence gates in force. Skipping is
 *   the least surprising behaviour — suppressing instead would drop readings
 *   that nothing is actually wrong with.
 *
 *   Absence is NOT rare, and not confined to newly listed names: 11% of
 *   tickers clearing the high-side gates have no realized vol (measured
 *   2026-08-12 — 247 of 2203). Nearly all of them are ordinary, liquid names
 *   that simply hold 14-15 bars in the lookback window against the producing
 *   query's 15-return floor, because `historical_prices` coverage is thinner
 *   than a trading calendar implies. So one row in nine is ungated: treat the
 *   low-side gate as a filter that catches most bad IV, never as a guarantee
 *   that a displayed move has been sanity-checked.
 * @returns {number|null} percent (7.2 means ±7.2%)
 */
export function impliedMovePct({ callIvAvg, putIvAvg, snapshotDate, expirationDate, realizedVol }) {
	if (!isPositiveNumber(callIvAvg) || !isPositiveNumber(putIvAvg)) return null;
	if (!snapshotDate || !expirationDate) return null;
	if (callIvAvg > MAX_CREDIBLE_IV || putIvAvg > MAX_CREDIBLE_IV) return null;
	if (Math.max(callIvAvg, putIvAvg) / Math.min(callIvAvg, putIvAvg) > MAX_IV_DIVERGENCE) return null;

	const iv = (callIvAvg + putIvAvg) / 2;
	if (isPositiveNumber(realizedVol) && iv / realizedVol < MIN_IV_TO_REALIZED) return null;

	const days = (Date.parse(expirationDate) - Date.parse(snapshotDate)) / 86400000;
	if (!Number.isFinite(days) || days <= 0) return null;
	if (days > MAX_IMPLIED_MOVE_HORIZON_DAYS) return null;

	return iv * Math.sqrt(days / 365) * IMPLIED_MOVE_CALIBRATION * 100;
}

/**
 * Combine whatever components are available into a single 0-100 bar score.
 *
 * Missing components drop out of BOTH the weighted numerator and the
 * denominator. Scoring an absent input as 0 would drag every incomplete row
 * toward "washed out" — the same NULL-dilution bug that halved sector momentum
 * in issue #94 (`sum += x || 0` against a shared row counter). Absence is not
 * a measurement of zero.
 *
 * @param {object} inputs
 * @param {number} [inputs.price]           current price
 * @param {number} [inputs.week52Low]
 * @param {number} [inputs.week52High]
 * @param {number} [inputs.runUp]           4-week price change as a fraction (0.12 = +12%)
 * @param {number} [inputs.marketFearGreed] 0-100, greed high
 * @param {number} [inputs.sectorScore]     0-100, greed high
 * @param {number} [inputs.callOpenInterest]
 * @param {number} [inputs.putOpenInterest]
 * @param {number} [inputs.callIvAvg]
 * @param {number} [inputs.putIvAvg]
 * @param {string} [inputs.snapshotDate]
 * @param {string} [inputs.expirationDate]
 * @param {number} [inputs.realizedVol] annualised, as a fraction — see impliedMovePct
 * @returns {{
 *   score: number|null,
 *   rating: string|null,
 *   components: Record<string, number|null>,
 *   coverage: { available: number, total: number },
 *   impliedMovePct: number|null
 * }}
 */
export function computeExpectationsBar(inputs = {}) {
	const {
		price, week52Low, week52High, runUp,
		marketFearGreed, sectorScore,
		callOpenInterest, putOpenInterest
	} = inputs;

	const components = {
		rangePosition: rangePositionScore(price, week52Low, week52High),
		runUp: runUpScore(runUp),
		optionsPositioning: optionsPositioningScore(callOpenInterest, putOpenInterest),
		marketSentiment: sentimentScore(marketFearGreed),
		sectorSentiment: sentimentScore(sectorScore)
	};

	let weighted = 0;
	let weightSum = 0;
	let available = 0;

	for (const [key, weight] of Object.entries(COMPONENT_WEIGHTS)) {
		const value = components[key];
		if (value === null) continue;
		weighted += value * weight;
		weightSum += weight;
		available += 1;
	}

	const score = weightSum > 0 ? Math.round(weighted / weightSum) : null;

	return {
		score,
		rating: score === null ? null : barRatingFor(score),
		components,
		coverage: { available, total: COMPONENT_COUNT },
		impliedMovePct: impliedMovePct(inputs)
	};
}
